import { useEffect, useState } from 'react';
import { 
  Settings, Shield, Download, History, Users, AlertTriangle,
  CheckCircle, RefreshCw, Database, Lock
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminSettings = () => {
  const [securityStats, setSecurityStats] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const headers = { Authorization: `Bearer ${token}` };

      const [statsRes, logsRes] = await Promise.all([
        axios.get(`${API}/admin/security/stats`, { headers }),
        axios.get(`${API}/admin/audit-logs?limit=20`, { headers })
      ]);

      setSecurityStats(statsRes.data);
      setAuditLogs(logsRes.data.logs || []);
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Failed to load settings data');
    } finally {
      setLoading(false);
    }
  };

  const handleExportBackup = async () => {
    try {
      setExporting(true);
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(`${API}/admin/backup/export`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Download as JSON file
      const dataStr = JSON.stringify(response.data, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `jat_backup_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success('Backup exported successfully');
      loadData(); // Refresh to show new audit log entry
    } catch (error) {
      console.error('Error exporting backup:', error);
      toast.error('Failed to export backup');
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-settings">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-light text-charcoal flex items-center gap-2">
            <Settings className="w-6 h-6 text-gold" />
            Settings & Security
          </h1>
          <p className="text-charcoal-light mt-1">System settings, security, and data backup</p>
        </div>
        <button
          onClick={loadData}
          className="flex items-center gap-2 px-4 py-2 border border-charcoal/20 text-charcoal hover:bg-beige"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Security Overview */}
      <div className="bg-white border border-charcoal/10">
        <div className="p-4 border-b border-charcoal/10 flex items-center gap-2">
          <Shield className="w-5 h-5 text-gold" />
          <h2 className="font-heading text-lg text-charcoal">Security Overview</h2>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-beige/30">
              <Users className="w-8 h-8 mx-auto text-charcoal-light mb-2" />
              <p className="text-2xl font-heading text-charcoal">{securityStats?.total_users || 0}</p>
              <p className="text-xs text-charcoal-light">Total Users</p>
            </div>
            <div className="text-center p-4 bg-beige/30">
              <Lock className="w-8 h-8 mx-auto text-gold mb-2" />
              <p className="text-2xl font-heading text-charcoal">{securityStats?.admin_users || 0}</p>
              <p className="text-xs text-charcoal-light">Admin Users</p>
            </div>
            <div className="text-center p-4 bg-beige/30">
              <History className="w-8 h-8 mx-auto text-blue-500 mb-2" />
              <p className="text-2xl font-heading text-charcoal">{securityStats?.recent_admin_actions_24h || 0}</p>
              <p className="text-xs text-charcoal-light">Actions (24h)</p>
            </div>
            <div className="text-center p-4 bg-beige/30">
              {securityStats?.security_status === 'healthy' ? (
                <CheckCircle className="w-8 h-8 mx-auto text-green-500 mb-2" />
              ) : (
                <AlertTriangle className="w-8 h-8 mx-auto text-orange-500 mb-2" />
              )}
              <p className="text-lg font-heading text-charcoal capitalize">{securityStats?.security_status || 'Unknown'}</p>
              <p className="text-xs text-charcoal-light">System Status</p>
            </div>
          </div>
        </div>
      </div>

      {/* Data Backup */}
      <div className="bg-white border border-charcoal/10">
        <div className="p-4 border-b border-charcoal/10 flex items-center gap-2">
          <Database className="w-5 h-5 text-gold" />
          <h2 className="font-heading text-lg text-charcoal">Data Backup</h2>
        </div>
        <div className="p-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <p className="text-charcoal mb-1">Export your store data for backup purposes</p>
              <p className="text-sm text-charcoal-light">
                Includes: Products, Categories, Orders, Users (without passwords), Coupons
              </p>
            </div>
            <button
              onClick={handleExportBackup}
              disabled={exporting}
              className="flex items-center gap-2 px-6 py-3 bg-gold text-white hover:bg-gold-dark disabled:opacity-50"
            >
              {exporting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  Export Backup
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Audit Logs */}
      <div className="bg-white border border-charcoal/10">
        <div className="p-4 border-b border-charcoal/10 flex items-center gap-2">
          <History className="w-5 h-5 text-gold" />
          <h2 className="font-heading text-lg text-charcoal">Recent Admin Activity</h2>
        </div>
        {auditLogs.length > 0 ? (
          <div className="divide-y divide-charcoal/5">
            {auditLogs.map((log) => (
              <div key={log.id} className="p-4 flex items-start justify-between">
                <div>
                  <p className="text-sm text-charcoal">
                    <span className="font-medium">{log.user_email}</span>
                    <span className="mx-2">•</span>
                    <span className="capitalize">{log.action_type}</span>
                  </p>
                  <p className="text-sm text-charcoal-light">{log.action_detail}</p>
                  {log.entity_type && (
                    <p className="text-xs text-charcoal-light mt-1">
                      {log.entity_type}: {log.entity_id}
                    </p>
                  )}
                </div>
                <p className="text-xs text-charcoal-light whitespace-nowrap">
                  {formatDate(log.created_at)}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center text-charcoal-light">
            No admin activity recorded yet
          </div>
        )}
      </div>

      {/* Store Info */}
      <div className="bg-white border border-charcoal/10">
        <div className="p-4 border-b border-charcoal/10">
          <h2 className="font-heading text-lg text-charcoal">Store Information</h2>
        </div>
        <div className="p-4 grid md:grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-charcoal-light">Store Name</p>
            <p className="text-charcoal">JAT Fashion House</p>
          </div>
          <div>
            <p className="text-xs text-charcoal-light">Domain</p>
            <p className="text-charcoal">jatfashionhouse.com</p>
          </div>
          <div>
            <p className="text-xs text-charcoal-light">Currency</p>
            <p className="text-charcoal">INR (₹)</p>
          </div>
          <div>
            <p className="text-xs text-charcoal-light">Timezone</p>
            <p className="text-charcoal">Asia/Kolkata (IST)</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
